import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield, Smartphone, Wifi, Car, Radio, BrainCircuit, Eye,
  Activity, TrendingUp, Download, RefreshCw, Globe, Zap,
  BarChart3, DollarSign, Users, Server, ArrowLeft,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, sub, color = 'orange', delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="bg-[#242424] border border-white/10 rounded-xl p-5 hover:border-orange-500/30 transition-all"
  >
    <div className="flex items-start justify-between mb-3">
      <div className={`p-2.5 rounded-lg ${
        color === 'green' ? 'bg-green-500/10' :
        color === 'blue' ? 'bg-blue-500/10' :
        color === 'purple' ? 'bg-purple-500/10' :
        color === 'red' ? 'bg-red-500/10' :
        'bg-orange-500/10'
      }`}>
        <Icon className={`w-5 h-5 ${
          color === 'green' ? 'text-green-400' :
          color === 'blue' ? 'text-blue-400' :
          color === 'purple' ? 'text-purple-400' :
          color === 'red' ? 'text-red-400' :
          'text-orange-400'
        }`} />
      </div>
      <Activity className="w-3.5 h-3.5 text-green-400 animate-pulse" />
    </div>
    <p className="text-2xl font-bold text-white mb-0.5">{value}</p>
    <p className="text-sm text-neutral-400">{label}</p>
    {sub && <p className="text-xs text-neutral-600 mt-1">{sub}</p>}
  </motion.div>
);

const ModuleCard = ({ name, metrics, icon: Icon, color, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="bg-[#242424] border border-white/10 rounded-xl p-5 hover:border-orange-500/20 transition-all"
  >
    <div className="flex items-center gap-3 mb-4">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <h3 className="font-semibold text-white text-sm">{name}</h3>
    </div>
    <div className="space-y-2.5">
      {metrics.map(([label, val]) => (
        <div key={label} className="flex justify-between items-center">
          <span className="text-xs text-neutral-500">{label}</span>
          <span className="text-sm font-semibold text-white">{val}</span>
        </div>
      ))}
    </div>
  </motion.div>
);

const fmt = (n) => {
  if (n >= 1e9) return (n / 1e9).toFixed(1) + 'B';
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return n?.toLocaleString?.() || n;
};

export default function LiveMetricsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/securesphere/live-metrics`);
      if (res.ok) {
        const json = await res.json();
        setData(json);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Metrics fetch failed:', error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, 8000);
    return () => clearInterval(interval);
  }, [fetchMetrics]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-orange-400 animate-spin" />
      </div>
    );
  }

  const p = data?.platform || {};
  const m = data?.modules || {};
  const f = data?.financials || {};

  return (
    <div className="min-h-screen bg-[#1a1a1a] text-white">
      {/* Header */}
      <div className="bg-[#242424] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80 transition" data-testid="metrics-home-btn">
              <img src="/infuse-logo.jpg" alt="Infuse.AI" className="h-8 w-8 rounded-lg" />
              <span className="text-lg font-bold hidden sm:inline"><span className="text-white">Infuse</span><span className="text-[#F26419]">.AI</span><span className="text-neutral-400 text-xs align-top">&trade;</span></span>
            </button>
            <div className="h-5 w-px bg-white/10" />
            <span className="text-sm font-semibold text-orange-400">Live Platform Metrics</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-xs text-neutral-500">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              {lastUpdate && <span>Updated {lastUpdate.toLocaleTimeString()}</span>}
            </div>
            <button onClick={fetchMetrics} className="p-2 hover:bg-white/5 rounded-lg transition" data-testid="refresh-metrics-btn">
              <RefreshCw className="w-4 h-4 text-neutral-400" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Hero Stats */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">SecureSphere Live Dashboard</h1>
          <p className="text-neutral-500">Real-time metrics across all security modules — auto-refreshing every 8 seconds</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
          <StatCard icon={Shield} label="Threats Blocked" value={fmt(p.total_threats_blocked)} sub="All time" color="red" delay={0} />
          <StatCard icon={Server} label="Devices Protected" value={fmt(p.active_devices_protected)} sub="Active now" color="blue" delay={0.05} />
          <StatCard icon={Zap} label="Avg Response" value={`${p.avg_response_ms}ms`} sub="Real-time" color="green" delay={0.1} />
          <StatCard icon={Globe} label="Events Today" value={fmt(p.events_processed_today)} sub="Processed" color="purple" delay={0.15} />
          <StatCard icon={TrendingUp} label="Uptime" value={`${p.uptime_percent}%`} sub="Platform SLA" color="green" delay={0.2} />
          <StatCard icon={Users} label="Active Customers" value={p.active_customers} sub="Across tiers" delay={0.25} />
        </div>

        {/* Module Metrics Grid */}
        <h2 className="text-xl font-bold mb-5 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-orange-400" />
          Module Metrics (Live 24h)
        </h2>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <ModuleCard
            name="Mobile Protection"
            icon={Smartphone}
            color="bg-blue-600"
            delay={0}
            metrics={[
              ['Threats Blocked (24h)', fmt(m.mobile_protection?.threats_blocked_24h)],
              ['Devices Monitored', fmt(m.mobile_protection?.devices_monitored)],
              ['Phishing URLs Caught', fmt(m.mobile_protection?.phishing_urls_caught)],
              ['SMS Fraud Prevented', fmt(m.mobile_protection?.sms_fraud_prevented)],
              ['Detection Rate', `${m.mobile_protection?.detection_rate}%`],
            ]}
          />
          <ModuleCard
            name="IoT Security"
            icon={Wifi}
            color="bg-green-600"
            delay={0.05}
            metrics={[
              ['Anomalies Detected (24h)', fmt(m.iot_security?.anomalies_detected_24h)],
              ['Devices Registered', fmt(m.iot_security?.devices_registered)],
              ['Firmware Scans', fmt(m.iot_security?.firmware_scans)],
              ['Botnet Blocked', fmt(m.iot_security?.botnet_attempts_blocked)],
              ['Devices Patched', fmt(m.iot_security?.vulnerable_devices_patched)],
            ]}
          />
          <ModuleCard
            name="Automotive Security"
            icon={Car}
            color="bg-purple-600"
            delay={0.1}
            metrics={[
              ['CAN Anomalies (24h)', fmt(m.automotive_security?.can_anomalies_24h)],
              ['Vehicles Monitored', fmt(m.automotive_security?.vehicles_monitored)],
              ['GPS Spoof Blocked', fmt(m.automotive_security?.gps_spoof_blocked)],
              ['ECU Vulns Found', fmt(m.automotive_security?.ecu_vulns_found)],
              ['OTA Updates Secured', fmt(m.automotive_security?.ota_updates_secured)],
            ]}
          />
          <ModuleCard
            name="GSM Fraud Detection"
            icon={Radio}
            color="bg-red-600"
            delay={0.15}
            metrics={[
              ['Fraud Blocked (24h)', fmt(m.gsm_fraud?.fraud_blocked_24h)],
              ['SIM Swaps Prevented', fmt(m.gsm_fraud?.sim_swaps_prevented)],
              ['IMSI Catchers Found', fmt(m.gsm_fraud?.imsi_catchers_detected)],
              ['Roaming Fraud Saved', `$${fmt(m.gsm_fraud?.roaming_fraud_saved_usd)}`],
              ['Subscribers Protected', fmt(m.gsm_fraud?.subscribers_protected)],
            ]}
          />
          <ModuleCard
            name="AI Security Platform"
            icon={BrainCircuit}
            color="bg-amber-600"
            delay={0.2}
            metrics={[
              ['Prompt Injections Blocked', fmt(m.ai_security?.prompt_injections_blocked)],
              ['API Calls Monitored (24h)', fmt(m.ai_security?.api_calls_monitored_24h)],
              ['PII Leaks Prevented', fmt(m.ai_security?.pii_leaks_prevented)],
              ['Agent Actions Audited', fmt(m.ai_security?.agent_actions_audited)],
              ['Compliance Score', `${m.ai_security?.compliance_score}%`],
            ]}
          />
          <ModuleCard
            name="Telecom Signaling"
            icon={Globe}
            color="bg-cyan-600"
            delay={0.25}
            metrics={[
              ['SS7 Attacks Blocked (24h)', fmt(m.telecom_signaling?.ss7_attacks_blocked_24h)],
              ['Diameter Events (24h)', fmt(m.telecom_signaling?.diameter_events_24h)],
              ['GTP Anomalies', fmt(m.telecom_signaling?.gtp_anomalies)],
              ['Signaling Uptime', `${m.telecom_signaling?.signaling_uptime}%`],
              ['Carriers Protected', m.telecom_signaling?.carriers_protected],
            ]}
          />
          <ModuleCard
            name="Surveillance & IoT"
            icon={Eye}
            color="bg-indigo-600"
            delay={0.3}
            metrics={[
              ['Cameras Monitored', fmt(m.surveillance_iot?.cameras_monitored)],
              ['Smart Meters Online', fmt(m.surveillance_iot?.smart_meters_online)],
              ['Dashcams Tracked', fmt(m.surveillance_iot?.dashcams_tracked)],
              ['Alerts Generated (24h)', fmt(m.surveillance_iot?.alerts_generated_24h)],
              ['Network Scans Today', fmt(m.surveillance_iot?.network_scans_today)],
            ]}
          />
          <ModuleCard
            name="NetFlow Analytics"
            icon={BarChart3}
            color="bg-teal-600"
            delay={0.35}
            metrics={[
              ['Flows Analyzed (24h)', fmt(m.netflow_analytics?.flows_analyzed_24h)],
              ['Bandwidth Monitored', `${m.netflow_analytics?.bandwidth_monitored_gbps} Gbps`],
              ['Anomalous Flows', fmt(m.netflow_analytics?.anomalous_flows)],
              ['Capacity Utilization', `${m.netflow_analytics?.capacity_utilization}%`],
              ['Cost Savings (Monthly)', `$${fmt(m.netflow_analytics?.cost_savings_monthly_usd)}`],
            ]}
          />
        </div>

        {/* Financial Metrics */}
        <h2 className="text-xl font-bold mb-5 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-400" />
          Financial Metrics (Live)
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard icon={DollarSign} label="Monthly Recurring Revenue" value={`$${fmt(f.mrr)}`} color="green" delay={0} />
          <StatCard icon={TrendingUp} label="ARR Run Rate" value={`$${fmt(f.arr_run_rate)}`} color="green" delay={0.05} />
          <StatCard icon={Users} label="Customers" value={`${f.customers_starter}S / ${f.customers_enterprise}E / ${f.customers_carrier}C`} sub="Starter / Enterprise / Carrier" delay={0.1} />
          <StatCard icon={BarChart3} label="Gross Margin" value={`${f.gross_margin_pct}%`} sub={`NRR: ${f.nrr_pct}% | CAC Payback: ${f.avg_cac_payback_months}mo`} color="green" delay={0.15} />
        </div>

        {/* Download Section */}
        <div className="bg-[#242424] border border-white/10 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-bold mb-4">Download Financial Documents</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <a
              href="/SecureSphere_Financial_Model.xlsx"
              download
              data-testid="download-xlsx-btn"
              className="flex items-center gap-3 p-4 bg-white/5 rounded-lg border border-white/10 hover:border-green-500/30 transition group"
            >
              <Download className="w-5 h-5 text-green-400 group-hover:scale-110 transition-transform" />
              <div>
                <p className="font-semibold text-sm">Financial Model (.xlsx)</p>
                <p className="text-xs text-neutral-500">6-sheet detailed spreadsheet</p>
              </div>
            </a>
            <a
              href="/SecureSphere_Financial_Model.pptx"
              download
              data-testid="download-financial-pptx-btn"
              className="flex items-center gap-3 p-4 bg-white/5 rounded-lg border border-white/10 hover:border-orange-500/30 transition group"
            >
              <Download className="w-5 h-5 text-orange-400 group-hover:scale-110 transition-transform" />
              <div>
                <p className="font-semibold text-sm">Financial Deck (.pptx)</p>
                <p className="text-xs text-neutral-500">9-slide investor presentation</p>
              </div>
            </a>
            <a
              href="/SecureSphere_Sales_Deck.pptx"
              download
              data-testid="download-sales-pptx-btn"
              className="flex items-center gap-3 p-4 bg-white/5 rounded-lg border border-white/10 hover:border-blue-500/30 transition group"
            >
              <Download className="w-5 h-5 text-blue-400 group-hover:scale-110 transition-transform" />
              <div>
                <p className="font-semibold text-sm">Sales Deck (.pptx)</p>
                <p className="text-xs text-neutral-500">15-slide sales presentation</p>
              </div>
            </a>
          </div>
        </div>

        <div className="text-center text-neutral-600 text-xs pb-8">
          &copy; 2026 Infuse Technologies. All rights reserved. Data refreshes every 8 seconds.
        </div>
      </div>
    </div>
  );
}
